import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "model", "modelHidden",
    "variant", "variantHidden", "variantCol",
    "info", "typeName", "version", "weightRange", "cgRange", "wbm",
    "config", "weightVariant", "weightVariantCol", "serialNumber"
  ]

  static values = { configs: Array }

  connect() {
    // If model is pre-selected (edit mode), populate variant dropdown and info
    const currentModel = this.modelHiddenTarget.value
    if (currentModel) {
      this.populateVariants(currentModel)
      this.updateInfo()
    }
  }

  modelChanged() {
    const model = this.modelTarget.value
    this.modelHiddenTarget.value = model

    this.populateVariants(model)
    this.variantHiddenTarget.value = ""

    this.updateInfo()
  }

  variantChanged() {
    this.variantHiddenTarget.value = this.variantTarget.value
    this.updateInfo()
  }

  configChanged() {
    this.populateWeightVariants(this.selectedConfig())
    this.updateInfo()
  }

  weightVariantChanged() {
    this.updateInfo()
  }

  serialNumberChanged() {
    this.populateWeightVariants(this.selectedConfig())
  }

  populateVariants(model) {
    const variants = this.configsValue
      .filter(c => c.model === model && c.variant)
      .map(c => c.variant)

    if (variants.length > 0) {
      this.variantColTarget.style.display = ""
      const currentVariant = this.variantHiddenTarget.value

      let html = '<option value="">Select the variant…</option>'
      variants.forEach(v => {
        const selected = v === currentVariant ? " selected" : ""
        html += `<option value="${v}"${selected}>${v}</option>`
      })
      this.variantTarget.innerHTML = html
    } else {
      this.variantColTarget.style.display = "none"
      this.variantTarget.innerHTML = '<option value="">Base model (no variant)</option>'
      this.variantHiddenTarget.value = ""
    }
  }

  updateInfo() {
    const model = this.modelHiddenTarget.value
    const variant = this.variantHiddenTarget.value

    if (!model) {
      this.infoTarget.style.display = "none"
      return
    }

    this.populateConfigurations()
    const config = this.selectedConfig()

    if (config) {
      this.infoTarget.style.display = ""
      const label = [config.manufacturer, model, variant].filter(Boolean).join(" ")
      this.typeNameTarget.textContent = label
      this.versionTarget.textContent = config.version || "—"
      this.populateWeightVariants(config)

      if ((config.weight_variants || []).length > 0) {
        this.weightRangeTarget.textContent = `${config.weight_variants.length} WVs`
      } else if (config.weight_min && config.weight_max) {
        const minT = Math.round(config.weight_min / 1000)
        const maxT = Math.round(config.weight_max / 1000)
        this.weightRangeTarget.textContent = `${minT}t – ${maxT}t`
      } else {
        this.weightRangeTarget.textContent = "—"
      }

      if ((config.weight_variants || []).length > 0) {
        this.cgRangeTarget.textContent = "Takeoff · flight · landing"
      } else if (config.cg_fwd != null && config.cg_aft != null) {
        this.cgRangeTarget.textContent = `${config.cg_fwd}% – ${config.cg_aft}%`
      } else {
        this.cgRangeTarget.textContent = "—"
      }

      this.wbmTarget.textContent = config.wbm || "—"
    } else {
      this.infoTarget.style.display = "none"
    }
  }

  compatibleConfigs() {
    const model = this.modelHiddenTarget.value
    const variant = this.variantHiddenTarget.value
    const exact = this.configsValue.filter(c => c.model === model && c.variant === variant)
    if (exact.length > 0) return exact
    return this.configsValue.filter(c => c.model === model && !c.variant)
  }

  populateConfigurations() {
    const configs = this.compatibleConfigs()
    const previous = this.configTarget.value
    const unique = configs.filter((config, index, all) =>
      all.findIndex(candidate => candidate.version === config.version) === index
    )
    this.configTarget.innerHTML = '<option value="">Select a reviewed profile…</option>' + unique.map(config => {
      const selected = config.version === previous ? " selected" : ""
      return `<option value="${config.version}"${selected}>${config.version} · ${config.wbm || "WBM not recorded"}</option>`
    }).join("")
    if (!this.configTarget.value && unique.length === 1) this.configTarget.value = unique[0].version
  }

  selectedConfig() {
    const configs = this.compatibleConfigs()
    return configs.find(config => config.version === this.configTarget.value) || configs[0]
  }

  populateWeightVariants(config) {
    let variants = config?.weight_variants || []
    const msn = Number((this.serialNumberTarget.value.match(/\d+/g) || []).join(""))
    const applicable = variants.filter(variant => (variant.applicable_msns || []).map(Number).includes(msn))
    if (applicable.length > 0) variants = applicable
    if (variants.length === 0) {
      this.weightVariantColTarget.style.display = "none"
      this.weightVariantTarget.required = false
      this.weightVariantTarget.innerHTML = '<option value="">Not applicable</option>'
      return
    }

    const previous = this.weightVariantTarget.value
    this.weightVariantColTarget.style.display = ""
    this.weightVariantTarget.required = true
    this.weightVariantTarget.innerHTML = '<option value="">Select the aircraft\'s published variant…</option>' + variants.map(variant => {
      const selected = variant.value === previous ? " selected" : ""
      return `<option value="${variant.value}"${selected}>${variant.label}</option>`
    }).join("")
    if (!this.weightVariantTarget.value && variants.length === 1) this.weightVariantTarget.value = variants[0].value
  }
}
